import React, { useEffect, useRef, useState } from 'react';
import GridLayout, { type Layout, type LayoutItem } from 'react-grid-layout';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, Label, LoadingSpinner, Select, TextInput, useConfirm } from '../../components/ui';
import {
  ChartTrendingUpIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  PlusIcon,
  SaveIcon,
  TrashIcon,
  ViewColumnsIcon,
} from '../../components/ui/Icons';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { createWidgetElement, getAllWidgetDefinitions } from '../dashboard/widgetRegistry';
import { saveEFormAppPage, deleteEFormAppPage } from '../../services/mutations';
import {
  type EFormAppPageWidget,
  useEFormAppPage,
  useEFormAppPages,
  useEFormDefinition,
  useEFormSavedReports,
  usePublishedEForms,
} from './useData';
import type { EFormSidebarPlacement } from './sidebarPlacements';
import { EFORM_SAVED_REPORT_WIDGET_ID, SavedReportWidget } from './SavedReportWidget';

export { EFORM_SAVED_REPORT_WIDGET_ID, SavedReportWidget };

const REPORT_WIDGET = {
  id: EFORM_SAVED_REPORT_WIDGET_ID,
  name: 'Saved report',
  description: 'A pivot or chart you already saved for this form',
  defaultSize: { w: 6, h: 3 },
  defaultSettings: {},
};

const EFORM_PAGE_WIDGET_IDS = [
  'eforms-recent-submissions',
  'eforms-approval-pending',
  'eforms-catalog-counters',
  'eforms-submission-status',
  'eforms-submissions-trend',
  REPORT_WIDGET.id,
] as const;

const EFORM_WIDGET_COPY: Record<string, { name: string; description: string }> = {
  'eforms-recent-submissions': {
    name: 'Recent submissions',
    description: 'Latest records filled for this form',
  },
  'eforms-approval-pending': {
    name: 'Pending approvals',
    description: 'Submissions waiting for a decision',
  },
  'eforms-catalog-counters': {
    name: 'Submission counters',
    description: 'Totals for this form',
  },
  'eforms-submission-status': {
    name: 'Status mix',
    description: 'Draft vs submitted breakdown chart',
  },
  'eforms-submissions-trend': {
    name: 'Submissions trend',
    description: 'Daily volume chart for the last 2 weeks',
  },
  [REPORT_WIDGET.id]: {
    name: REPORT_WIDGET.name,
    description: REPORT_WIDGET.description,
  },
};

const EFORM_WIDGET_ICONS: Record<string, React.ReactNode> = {
  'eforms-recent-submissions': <ClockIcon className="h-5 w-5" />,
  'eforms-approval-pending': <CheckCircleIcon className="h-5 w-5" />,
  'eforms-catalog-counters': <ViewColumnsIcon className="h-5 w-5" />,
  'eforms-submission-status': <ChartTrendingUpIcon className="h-5 w-5" />,
  'eforms-submissions-trend': <ChartTrendingUpIcon className="h-5 w-5" />,
  [REPORT_WIDGET.id]: <DocumentTextIcon className="h-5 w-5" />,
};

type PageWidgetOption = {
  id: string;
  name: string;
  description: string;
  defaultSize: { w: number; h: number };
  defaultSettings: Record<string, unknown>;
};

function pageWidgetOptions(embedded: boolean): PageWidgetOption[] {
  const fromRegistry = getAllWidgetDefinitions().map((widget) => ({
    id: widget.id,
    name: EFORM_WIDGET_COPY[widget.id]?.name ?? widget.name,
    description: EFORM_WIDGET_COPY[widget.id]?.description ?? widget.description,
    defaultSize: widget.defaultSize,
    defaultSettings: widget.defaultSettings,
  }));
  const report: PageWidgetOption = {
    id: REPORT_WIDGET.id,
    name: EFORM_WIDGET_COPY[REPORT_WIDGET.id].name,
    description: EFORM_WIDGET_COPY[REPORT_WIDGET.id].description,
    defaultSize: REPORT_WIDGET.defaultSize,
    defaultSettings: REPORT_WIDGET.defaultSettings,
  };
  const eform = [
    ...fromRegistry.filter((widget) =>
      EFORM_PAGE_WIDGET_IDS.includes(widget.id as (typeof EFORM_PAGE_WIDGET_IDS)[number]),
    ),
    report,
  ];
  if (embedded) return eform;
  return [
    ...eform,
    ...fromRegistry.filter(
      (widget) => !EFORM_PAGE_WIDGET_IDS.includes(widget.id as (typeof EFORM_PAGE_WIDGET_IDS)[number]),
    ),
  ];
}

function useContainerWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(320, entry.contentRect.width)));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

export const EFormAppPagesList: React.FC = () => {
  const { data, isLoading } = useEFormAppPages();
  if (isLoading) return <LoadingSpinner />;
  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Dashboard widgets</h1>
          <p className="text-sm text-slate-500">
            Configure form tiles for the central dashboard — not a separate dashboard per form.
          </p>
        </div>
        <Link to="/e-forms/apps/new"><Button><PlusIcon className="h-4 w-4" />New widget</Button></Link>
      </div>
      <div className="divide-y rounded-xl border bg-white dark:border-slate-700 dark:bg-slate-900">
        {data.map((page) => (
          <div key={page.id} className="flex items-center gap-3 p-3">
            <ViewColumnsIcon className="h-5 w-5 text-slate-500" />
            <div className="min-w-0 flex-1">
              <Link className="font-medium hover:underline" to={`/e-forms/apps/${page.id}`}>{page.name}</Link>
              <p className="truncate text-xs text-slate-500">{page.description || page.definition?.name || 'Dashboard widget'}</p>
            </div>
            <Link to={`/e-forms/apps/${page.id}/edit`}><Button variant="secondary" size="sm">Edit</Button></Link>
          </div>
        ))}
        {!data.length ? <p className="p-8 text-center text-sm text-slate-500">No dashboard widgets yet.</p> : null}
      </div>
    </div>
  );
};

export const EFormAppPageEditor: React.FC<{
  pageIdOverride?: number | 'new';
  definitionIdOverride?: number;
  embedded?: boolean;
  onDeleted?: () => void;
}> = ({ pageIdOverride, definitionIdOverride, embedded = false, onDeleted }) => {
  const rawId = useParams<{ pageId: string }>().pageId;
  const resolvedId = pageIdOverride ?? rawId;
  const pageId = resolvedId && resolvedId !== 'new' ? Number(resolvedId) : null;
  const { data: page } = useEFormAppPage(pageId);
  const { data: forms } = usePublishedEForms('', 1, 500);
  const scopedForm = useEFormDefinition(definitionIdOverride ?? 0);
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ref, width } = useContainerWidth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [definitionId, setDefinitionId] = useState<number | null>(definitionIdOverride ?? null);
  const [widgets, setWidgets] = useState<EFormAppPageWidget[]>([]);
  const [sidebarPlacements, setSidebarPlacements] = useState<EFormSidebarPlacement[]>([
    { mode: 'catalog_only' },
  ]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isNewPage = !pageId;
  const definitions = pageWidgetOptions(embedded);
  const { data: savedReports = [] } = useEFormSavedReports(definitionId);

  useEffect(() => {
    if (pageId || page) return;
    const requestedDefinitionId = definitionIdOverride ?? Number(searchParams.get('definitionId'));
    if (Number.isInteger(requestedDefinitionId) && requestedDefinitionId > 0) {
      if (!embedded) {
        navigate(
          `/e-forms/builder/${requestedDefinitionId}?section=pages&page=new`,
          { replace: true },
        );
        return;
      }
      setDefinitionId(requestedDefinitionId);
    }
  }, [definitionIdOverride, embedded, navigate, pageId, page, searchParams]);

  useEffect(() => {
    if (!page) return;
    setName(page.name);
    setDescription(page.description || '');
    setDefinitionId(
      definitionIdOverride && definitionIdOverride > 0
        ? definitionIdOverride
        : page.definitionId ?? null,
    );
    setWidgets(page.layoutJson?.widgets ?? []);
    setSidebarPlacements(
      Array.isArray(page.sidebarPlacements)
        ? (page.sidebarPlacements as EFormSidebarPlacement[])
        : [],
    );
  }, [page, definitionIdOverride]);

  useEffect(() => {
    if (embedded || !page?.definitionId || !pageId) return;
    navigate(
      `/e-forms/builder/${page.definitionId}?section=pages&page=${pageId}`,
      { replace: true },
    );
  }, [embedded, navigate, page?.definitionId, pageId]);

  const scopedTileSettings = (settings?: Record<string, unknown>) => ({
    ...(settings ?? {}),
    ...(definitionId ? { definitionId } : {}),
  });

  const addWidget = (type: string, settingsOverride?: Record<string, unknown>) => {
    const definition = definitions.find((row) => row.id === type);
    if (!definition) return;
    setWidgets((current) => [
      ...current,
      {
        id: `${type}-${Date.now()}`,
        type,
        x: 0,
        y: Infinity,
        ...definition.defaultSize,
        settings: {
          ...definition.defaultSettings,
          ...(definitionId ? { definitionId } : {}),
          ...settingsOverride,
        },
      },
    ]);
  };

  const addSavedReportTile = (reportId: number) => {
    addWidget(REPORT_WIDGET.id, {
      reportId,
      ...(definitionId ? { definitionId } : {}),
    });
  };

  const addOperationsStarter = () => {
    const stamp = Date.now();
    const scope = definitionId ? { definitionId } : {};
    setWidgets([
      {
        id: `eforms-recent-submissions-${stamp}`,
        type: 'eforms-recent-submissions',
        x: 0,
        y: 0,
        w: 6,
        h: 3,
        settings: { limit: 8, ...scope },
      },
      {
        id: `eforms-approval-pending-${stamp + 1}`,
        type: 'eforms-approval-pending',
        x: 6,
        y: 0,
        w: 6,
        h: 3,
        settings: { limit: 8, ...scope },
      },
    ]);
    if (!name.trim()) setName(scopedForm.data?.name ? `${scopedForm.data.name} operations` : 'Operations');
  };

  const addOverviewStarter = () => {
    const stamp = Date.now();
    const scope = definitionId ? { definitionId } : {};
    setWidgets([
      {
        id: `eforms-catalog-counters-${stamp}`,
        type: 'eforms-catalog-counters',
        x: 0,
        y: 0,
        w: 12,
        h: 2,
        settings: { ...scope },
      },
      {
        id: `eforms-recent-submissions-${stamp + 1}`,
        type: 'eforms-recent-submissions',
        x: 0,
        y: 2,
        w: 6,
        h: 3,
        settings: { limit: 8, ...scope },
      },
      {
        id: `eforms-submission-status-${stamp + 2}`,
        type: 'eforms-submission-status',
        x: 6,
        y: 2,
        w: 6,
        h: 3,
        settings: { ...scope },
      },
    ]);
    if (!name.trim()) setName(scopedForm.data?.name ? `${scopedForm.data.name} overview` : 'Overview');
  };

  const addAnalyticsStarter = () => {
    const stamp = Date.now();
    const scope = definitionId ? { definitionId } : {};
    const chartReport = savedReports.find((report) =>
      ['bar', 'line', 'pie'].includes(String(report.chartType || report.queryJson?.chartType || '')),
    );
    setWidgets([
      {
        id: `eforms-submissions-trend-${stamp}`,
        type: 'eforms-submissions-trend',
        x: 0,
        y: 0,
        w: 6,
        h: 3,
        settings: { days: 14, chartType: 'bar', ...scope },
      },
      {
        id: `eforms-submission-status-${stamp + 1}`,
        type: 'eforms-submission-status',
        x: 6,
        y: 0,
        w: 6,
        h: 3,
        settings: { ...scope },
      },
      chartReport
        ? {
            id: `eform-saved-report-${stamp + 2}`,
            type: REPORT_WIDGET.id,
            x: 0,
            y: 3,
            w: 12,
            h: 3,
            settings: { reportId: chartReport.id, ...scope },
          }
        : {
            id: `eforms-catalog-counters-${stamp + 2}`,
            type: 'eforms-catalog-counters',
            x: 0,
            y: 3,
            w: 12,
            h: 2,
            settings: { ...scope },
          },
    ]);
    if (!name.trim()) setName(scopedForm.data?.name ? `${scopedForm.data.name} analytics` : 'Analytics');
  };

  const save = async () => {
    if (!name.trim()) return showError('Widget name is required');
    setSaving(true);
    try {
      const scopedWidgets = widgets.map((widget) => ({
        ...widget,
        settings: scopedTileSettings(widget.settings),
      }));
      const saved = await saveEFormAppPage(
        {
          name,
          description,
          definitionId,
          layoutJson: { widgets: scopedWidgets },
          sidebarPlacements,
          isShared: true,
        },
        pageId ?? undefined,
      );
      setWidgets(scopedWidgets);
      showSuccess('Dashboard widget saved');
      navigate(
        embedded && definitionId
          ? `/e-forms/builder/${definitionId}?section=pages&page=${saved.id}`
          : searchParams.get('studio') === '1'
          ? `/e-forms/apps/${saved.id}/edit?studio=1`
          : `/e-forms/apps/${saved.id}/edit`,
        { replace: true },
      );
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Could not save dashboard widget');
    } finally {
      setSaving(false);
    }
  };

  const removePage = async () => {
    if (!pageId) return;
    const ok = await confirm({
      title: 'Delete dashboard widget?',
      message: `“${name || 'This widget'}” will be removed from the central dashboard and menu.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteEFormAppPage(pageId);
      showSuccess('Dashboard widget deleted');
      if (onDeleted) {
        onDeleted();
        return;
      }
      navigate(
        definitionId
          ? `/e-forms/builder/${definitionId}?section=pages`
          : '/e-forms/apps',
      );
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Could not delete dashboard widget');
    } finally {
      setDeleting(false);
    }
  };

  const layout: Layout = widgets.map(({ id, x, y, w, h }) => ({ i: id, x, y, w, h }));
  const formName = scopedForm.data?.name;
  const showGuide = widgets.length === 0;
  const starterTiles = definitions;

  const toolbar = (
    <div className={cn('overflow-hidden rounded-xl border', colors.border, colors.surface)}>
      <div className={cn('grid gap-3 p-4', embedded ? 'sm:grid-cols-2' : 'lg:grid-cols-3')}>
        <div>
          <Label required>Widget name</Label>
          <TextInput
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={formName ? `${formName} widget` : 'e.g. Safety inspections'}
          />
        </div>
        <div>
          <Label>Description <span className={colors.textMuted}>(optional)</span></Label>
          <TextInput
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What this dashboard tile shows"
          />
        </div>
        {!embedded ? (
          <div>
            <Label>Form</Label>
            <Select
              value={definitionId ?? ''}
              onChange={(event) => setDefinitionId(event.target.value ? Number(event.target.value) : null)}
              options={[{ value: '', label: 'Shared' }, ...forms.map((form) => ({ value: form.id, label: form.name }))]}
            />
          </div>
        ) : null}
      </div>
      <div className={cn('flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3', colors.border)}>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Select
            aria-label="Add tile"
            value=""
            placeholder="Add tile"
            showClearButton={false}
            onChange={(event) => addWidget(event.target.value)}
            options={definitions.map((widget) => ({ value: widget.id, label: widget.name }))}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pageId ? (
            <Button
              variant="ghost"
              onClick={() => void removePage()}
              disabled={saving || deleting}
              aria-label="Delete widget"
              title="Delete widget"
              className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          ) : null}
          <Button onClick={() => void save()} disabled={saving || deleting}>
            <SaveIcon className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save widget'}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={embedded ? 'flex min-h-[32rem] flex-col gap-3' : 'flex h-full min-h-0 flex-col gap-3 p-4'}>
      {showGuide ? (
        <div className={cn('rounded-xl border p-5 sm:p-6', colors.border, colors.surface)}>
          <div className="flex items-start gap-3">
            <div className={cn('rounded-lg border p-2', colors.border)}>
              <ViewColumnsIcon className={cn('h-6 w-6', colors.textMuted)} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tight">
                {isNewPage ? 'Create a dashboard widget' : 'This widget has no tiles yet'}
              </h2>
              <p className={cn('mt-1 max-w-2xl text-sm', colors.textMuted)}>
                Widgets appear on the central dashboard{formName ? ` for ${formName}` : ''}.
                Name it, add tiles, then save — you do not create a separate dashboard for each form.
              </p>
            </div>
          </div>
          <ol className="mt-5 grid gap-2 sm:grid-cols-3">
            {[
              { step: '1', title: 'Name the widget', detail: 'This is the title on the dashboard.' },
              { step: '2', title: 'Add tiles', detail: 'Pick what the widget should show.' },
              { step: '3', title: 'Save', detail: 'Then place it on the central dashboard.' },
            ].map((item) => (
              <li key={item.step} className={cn('rounded-lg border px-3 py-2.5', colors.border)}>
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                  Step {item.step}
                </div>
                <div className="mt-0.5 text-sm font-medium">{item.title}</div>
                <div className={cn('text-xs', colors.textMuted)}>{item.detail}</div>
              </li>
            ))}
          </ol>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div>
              <Label required>Widget name</Label>
              <TextInput
                autoFocus
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={formName ? `${formName} widget` : 'e.g. Safety inspections'}
              />
            </div>
            <div>
              <Label>Description <span className={colors.textMuted}>(optional)</span></Label>
              <TextInput
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="What this dashboard tile shows"
              />
            </div>
          </div>
          <div className="mt-6">
            <h3 className="text-sm font-semibold">Add your first tiles</h3>
            <p className={cn('mt-1 text-xs', colors.textMuted)}>
              Start from a suggested layout, pick individual tiles, or drop in a saved report chart.
            </p>

            <p className={cn('mt-4 text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
              Suggested layouts
            </p>
            <div className="mt-2 grid gap-2.5 grid-cols-2 md:grid-cols-3">
              <button
                type="button"
                onClick={addOperationsStarter}
                className={cn(
                  'rounded-xl border p-3.5 text-left transition-colors',
                  colors.border,
                  colors.interactiveHover,
                )}
              >
                <CheckCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div className="mt-2.5 text-sm font-semibold">Operations</div>
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  Recent submissions + pending approvals.
                </p>
              </button>
              <button
                type="button"
                onClick={addOverviewStarter}
                className={cn(
                  'rounded-xl border p-3.5 text-left transition-colors',
                  colors.border,
                  colors.interactiveHover,
                )}
              >
                <ViewColumnsIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div className="mt-2.5 text-sm font-semibold">Overview</div>
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  Counters, recent list, and status mix chart.
                </p>
              </button>
              <button
                type="button"
                onClick={addAnalyticsStarter}
                className={cn(
                  'rounded-xl border p-3.5 text-left transition-colors',
                  colors.border,
                  colors.interactiveHover,
                )}
              >
                <ChartTrendingUpIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div className="mt-2.5 text-sm font-semibold">Analytics</div>
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  Trend chart, status mix, and a saved report if available.
                </p>
              </button>
            </div>

            <p className={cn('mt-5 text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
              Individual tiles
            </p>
            <div className="mt-2 grid gap-2.5 grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {starterTiles.map((widget) => (
                <button
                  key={widget.id}
                  type="button"
                  onClick={() => addWidget(widget.id)}
                  className={cn(
                    'rounded-xl border p-3.5 text-left transition-colors',
                    colors.border,
                    colors.interactiveHover,
                  )}
                >
                  <span className={colors.textMuted}>{EFORM_WIDGET_ICONS[widget.id] ?? <PlusIcon className="h-5 w-5" />}</span>
                  <div className="mt-2.5 text-sm font-semibold">{widget.name}</div>
                  <p className={cn('mt-1 text-xs', colors.textMuted)}>{widget.description}</p>
                </button>
              ))}
            </div>

            {savedReports.length ? (
              <>
                <p className={cn('mt-5 text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                  Saved reports &amp; charts
                </p>
                <div className="mt-2 grid gap-2.5 grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                  {savedReports.map((report) => {
                    const chartType = String(report.chartType || report.queryJson?.chartType || 'table');
                    return (
                      <button
                        key={`report-${report.id}`}
                        type="button"
                        onClick={() => addSavedReportTile(report.id)}
                        className={cn(
                          'rounded-xl border p-3.5 text-left transition-colors',
                          colors.border,
                          colors.interactiveHover,
                        )}
                      >
                        <span className={colors.textMuted}><ChartTrendingUpIcon className="h-5 w-5" /></span>
                        <div className="mt-2.5 text-sm font-semibold">{report.name}</div>
                        <p className={cn('mt-1 text-xs', colors.textMuted)}>
                          {chartType === 'table' ? 'Saved table report' : `Saved ${chartType} chart`}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className={cn('mt-4 text-xs', colors.textMuted)}>
                Tip: save a pivot report under Reports to add chart tiles here.
              </p>
            )}
          </div>
          <div className={cn('mt-5 flex flex-wrap items-center justify-end gap-2 border-t pt-4', colors.border)}>
            <div className="flex flex-wrap items-center gap-2">
              {pageId ? (
                <Button
                  variant="ghost"
                  onClick={() => void removePage()}
                  disabled={saving || deleting}
                  aria-label="Delete widget"
                  title="Delete widget"
                  className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              ) : null}
              <Button onClick={() => void save()} disabled={saving || deleting}>
                <SaveIcon className="h-4 w-4" />
                {saving ? 'Saving…' : 'Save widget'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {toolbar}
          <p className={cn('text-xs', colors.textMuted)}>
            Drag tiles to rearrange. Use the corner to resize. Add more from the list, then save.
          </p>
          <div ref={ref} className="min-h-[28rem] flex-1 overflow-auto rounded-xl border bg-slate-50 dark:border-slate-700 dark:bg-slate-950">
            <GridLayout
              className="layout"
              width={width}
              layout={layout}
              gridConfig={{ cols: 12, rowHeight: 110, margin: [12, 12], containerPadding: [12, 12] }}
              onLayoutChange={(next) =>
                setWidgets((current) =>
                  current.map((widget) => {
                    const item = next.find((row) => row.i === widget.id);
                    return item ? { ...widget, x: item.x, y: item.y, w: item.w, h: item.h } : widget;
                  }),
                )
              }
            >
              {widgets.map((widget) => (
                <Card key={widget.id} className="flex h-full flex-col overflow-hidden p-0">
                  <div className={cn('flex items-center justify-between gap-2 border-b px-2 py-1', colors.border)}>
                    <span className={cn('truncate px-1 text-xs font-medium', colors.textMuted)}>
                      {definitions.find((item) => item.id === widget.type)?.name ?? widget.type}
                    </span>
                    <button
                      type="button"
                      className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-950/40"
                      onClick={() => setWidgets((current) => current.filter((row) => row.id !== widget.id))}
                      title="Remove"
                      aria-label="Remove"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="min-h-0 flex-1 overflow-auto p-3">
                    {widget.type === REPORT_WIDGET.id ? (
                      <SavedReportWidget
                        settings={scopedTileSettings(widget.settings)}
                        editing
                        onSettingsChange={(settings) =>
                          setWidgets((current) =>
                            current.map((row) =>
                              row.id === widget.id
                                ? { ...row, settings: scopedTileSettings(settings) }
                                : row,
                            ),
                          )
                        }
                      />
                    ) : (
                      createWidgetElement(widget.type, scopedTileSettings(widget.settings), true)
                    )}
                  </div>
                </Card>
              ))}
            </GridLayout>
          </div>
        </>
      )}
    </div>
  );
};

export const EFormAppPageView: React.FC = () => {
  const pageId = Number(useParams<{ pageId: string }>().pageId);
  const { data: page, isLoading } = useEFormAppPage(pageId);
  const { ref, width } = useContainerWidth();
  if (isLoading || !page) return <LoadingSpinner />;
  const widgets = page.layoutJson?.widgets ?? [];
  const pageDefinitionId = page.definitionId ?? null;
  const tileSettings = (settings?: Record<string, unknown>) => ({
    ...(settings ?? {}),
    ...(pageDefinitionId ? { definitionId: pageDefinitionId } : {}),
  });
  return (
    <div className="h-full overflow-auto p-4">
      <div className="mb-3 flex items-start justify-between">
        <div><h1 className="text-xl font-semibold">{page.name}</h1><p className="text-sm text-slate-500">{page.description}</p></div>
        <Link to={`/e-forms/apps/${page.id}/edit`}><Button variant="secondary" size="sm">Edit layout</Button></Link>
      </div>
      <div ref={ref}>
        <GridLayout
          width={width}
          layout={widgets.map((w): LayoutItem => ({ i: w.id, x: w.x, y: w.y, w: w.w, h: w.h }))}
          gridConfig={{ cols: 12, rowHeight: 110, margin: [12, 12], containerPadding: [0, 0] }}
          dragConfig={{ enabled: false }}
          resizeConfig={{ enabled: false }}
        >
          {widgets.map((widget) => (
            <Card key={widget.id} className="overflow-auto p-3">
              {widget.type === REPORT_WIDGET.id ? (
                <SavedReportWidget settings={tileSettings(widget.settings)} />
              ) : (
                createWidgetElement(widget.type, tileSettings(widget.settings))
              )}
            </Card>
          ))}
        </GridLayout>
      </div>
    </div>
  );
};
